/**
 * DEFUSE S11 — typed-first render routing (plan §4.3, render cutover).
 *
 * ONE shared seam for every live lift caller (`state/spatialBuilder.ts`,
 * `scene/SceneBulkLoad.ts`, `scene/StagePointerHandlers.ts`):
 *
 *   BEFORE:  getEntityById(id): DrawingEntity        // typed!
 *              → buildShellEntityPreview(entity)     // flatten to string bag (lossy)
 *              → entityToRenderable(bag, ctx)         // re-parse
 *   AFTER:   getEntityById(id): DrawingEntity
 *              → entityToRenderableTyped(entity, ctx) // typed, no bag round-trip
 *
 * # Routing contract
 *
 * A kind in {@link TYPED_RENDER_KINDS} renders through the typed dispatcher
 * and NEVER touches the bag — including a typed `null` result (Wipeout /
 * Revision Cloud adapters return `null` for degenerate geometry exactly like
 * their legacy twins; falling back on `null` would double-dispatch).
 *
 * A kind in {@link EXPLICIT_DISPATCH_RENDER_KINDS} ALSO has a real typed
 * adapter (no bag round-trip) but is dispatched explicitly by THIS function,
 * not through `entityToRenderableTyped`'s switch — see the module doc on
 * `typed/multileaderFromEntity.ts` (block-content flatten needs the shared
 * dispatcher internally, so registering Multileader IN that dispatcher would
 * close an import cycle) and `typed/blockFromEntity.ts` (Block, same
 * constraint, plus one-INSERT-flattens-to-MANY-renderables so it never fit
 * the single-renderable contract anyway).
 *
 * Everything else takes the legacy bag fallback:
 *
 *   - {@link BAG_FALLBACK_RENDER_KINDS} — kinds WITHOUT a typed adapter yet
 *     (Viewport, Rectangular Associative Array). Each lands in a later
 *     DEFUSE slice; the census test pins the set so it can ONLY SHRINK.
 *   - `Block` — flattens to MANY renderables via the typed `blockFromEntity`
 *     (`./typed/blockFromEntity`), through the ONE render call site
 *     `scene/liftBlockInsert.ts`: the legacy branch (`SceneBulkLoad`) pushes
 *     the children flat, and the cache-direct / zoom re-lift
 *     (`state/spatialBuilder.ts liftRenderableForId`) wraps them as
 *     `block-insert` satellites — BOTH dispatch Block BEFORE calling this
 *     helper. So for a typed Block THIS helper still falls through to the
 *     bag dispatcher's `case "Block": return null`, which is reached ONLY by
 *     the pick-candidate lift (`scene/StagePointerHandlers.ts`) — where a
 *     `null` deliberately falls back to the spatial stub whose bbox IS the
 *     pick geometry (`raycast/distance.ts` measures `block-insert` by bbox
 *     too), so no fidelity is lost — and by typed-resolution misses (no
 *     typed INSERT ⇒ nothing to flatten; the bag flatten was deleted in S11).
 *     Every RENDER path therefore draws a resolvable INSERT.
 *   - `Multileader` — see {@link EXPLICIT_DISPATCH_RENDER_KINDS} above: HAS
 *     a typed adapter and IS resolved by this helper (unlike `Block`), just
 *     via an explicit branch rather than `entityToRenderableTyped`.
 *   - typed-resolution miss (`typed === null`) — e.g. a preview whose id is
 *     not in the document. The draft-preview channel
 *     (`draft/draftPreviewLayer.tsx`) never enters this helper: an in-flight
 *     draft has NO typed document entity, so it stays on the legacy
 *     `entityToRenderable` until the command-engine emits typed drafts
 *     (S15/S16 — that channel is why the legacy per-kind adapters survive
 *     this cutover at all).
 *
 * The bag preview is built LAZILY (`buildPreview` closure) — a typed-covered
 * kind never pays the `buildShellEntityPreview` flatten cost.
 */

import type { EntityPreview, EntityType } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity } from "../types";

import type { AdapterContext } from "./adapterContext";
import { entityToRenderable } from "./index";
import { entityToRenderableTyped, TYPED_RENDER_KINDS } from "./typed/index";
import {
  insertAnnotativeHidden,
  resolveTypedAnnotation,
} from "./typed/annotativeResolve";
import { multileaderFromEntity } from "./typed/multileaderFromEntity";
import { tableFromEntity } from "./typed/tableFromEntity";
import { flattenBlockReference } from "./typed/blockFromEntity";

/** Resolver signature the lift callers inject (backed by `getEntityById`). */
export type TypedEntityResolver = (id: string) => DrawingEntity | null;

/**
 * DEFUSE S11 census — kinds still lifted through the legacy
 * `buildShellEntityPreview` → `entityToRenderable` bag pipeline because no
 * typed adapter exists yet. Pinned by
 * `__tests__/typedFirstRenderable.census.test.ts`; this set may ONLY SHRINK
 * (a kind leaves when its typed adapter slice lands: Dimension = S8 LANDED,
 * Multileader = S9 LANDED — see `EXPLICIT_DISPATCH_RENDER_KINDS`).
 */
export const BAG_FALLBACK_RENDER_KINDS: ReadonlySet<EntityType> =
  new Set<EntityType>(["Viewport", "Rectangular Associative Array"]);

/**
 * Kind rendered by the typed block flatten (`blockFromEntity`), not by either
 * single-renderable dispatcher (one INSERT → many renderables). Render
 * callers dispatch it through `scene/liftBlockInsert.ts` BEFORE this helper
 * (legacy: flat children; cache-direct: `block-insert` wrapper + satellites).
 */
export const TYPED_BLOCK_FLATTEN_KIND: EntityType = "Block";

/**
 * Lane H / S9 — kinds with a REAL typed adapter that this helper dispatches
 * EXPLICITLY (not via `entityToRenderableTyped`'s switch) to avoid an
 * import cycle through `typed/blockFromEntity.ts`. See the module doc above
 * and `typed/multileaderFromEntity.ts`'s own doc for the full rationale.
 * Currently just `Multileader`; `Block` is tracked separately via
 * {@link TYPED_BLOCK_FLATTEN_KIND} because it ALSO fails the
 * one-INSERT-to-one-renderable contract this helper has.
 */
export const EXPLICIT_DISPATCH_RENDER_KINDS: ReadonlySet<EntityType> =
  new Set<EntityType>(["Multileader", "Table"]);

/**
 * Typed-first single-entity lift. See the module header for the routing
 * contract. `buildPreview` is invoked ONLY on the fallback path.
 */
export function renderableFromDrawingEntity(args: {
  /** The canonical typed entity for this id, or `null` when unresolvable. */
  typed: DrawingEntity | null;
  /** Lazy legacy-bag builder — only consulted on the fallback path. */
  buildPreview: () => EntityPreview | null;
  ctx: AdapterContext;
}): RenderableEntity | null {
  const { typed, ctx } = args;
  if (typed && typed.type === "Table") {
    // ACAD_TABLE renders the anonymous `*T<N>` block that carries its graphics
    // — one table → many children, wrapped as ONE `block-insert` composite so
    // the single-renderable contract of this helper still holds. Dispatched
    // here (not through `entityToRenderableTyped`) for the same reason
    // Multileader is: the block-content flatten is INJECTED, and importing it
    // inside the shared dispatcher would close an import cycle through
    // `blockFromEntity.ts`.
    return tableFromEntity(typed, ctx, flattenBlockReference);
  }
  if (typed && typed.type === "Multileader") {
    // Lane H / S9 — explicit dispatch (see EXPLICIT_DISPATCH_RENDER_KINDS
    // doc above). Typed, no bag round-trip, just not via the shared switch.
    // MULTILEADER owns its OWN annotative-context picking inside the adapter
    // (`pickAnnotativeContext`), so the S10d visibility gate below skips it.
    // Codex PR #784 round 4 F1 — the block-content flatten is INJECTED (the
    // multileader modules no longer import it, which is what broke the cycle
    // that made `blockFromEntity` DROP a block-child MLEADER). A top-level
    // MLEADER gets the production entry verbatim.
    return multileaderFromEntity(typed, ctx, flattenBlockReference);
  }
  // S10d — annotative visibility gate. A top-level TEXT / MTEXT / HATCH that is
  // annotative but has NO representation at the active CANNOSCALE is hidden when
  // ANNOALLVISIBLE=false (mirrors the legacy SVG `if (!annoCtx.isVisible) return
  // null`). The typed adapters apply the per-scale geometry override for the
  // VISIBLE case; hiding is centralised here so the adapters keep their non-null
  // return (and the golden-parity harness, which builds a default ctx with no
  // active scale, is unaffected — `isVisible` is always true there). INSERT
  // (Block) render-visibility is ALSO owned by `blockFromEntity` (it flattens
  // to `[]`), and the render lift paths dispatch Block BEFORE this entry
  // (`liftRenderableForId` applies this same predicate first) — but
  // `typedAnnotativeHidden` covers `Block` here too so the cache-direct pick
  // handlers share ONE truth (round-19). ATTRIB visibility stays in
  // `attdefFromEntity` (returns `[]`), which runs outside this entry.
  if (typed && typedAnnotativeHidden(typed, ctx)) {
    return null;
  }
  if (typed && TYPED_RENDER_KINDS.has(typed.type)) {
    // Typed-covered kind: the dispatcher's result is FINAL (a typed `null`
    // matches the legacy twin's `null` for the same degenerate input —
    // golden-parity-pinned; no bag fallback).
    return entityToRenderableTyped(typed, ctx);
  }
  const preview = args.buildPreview();
  if (!preview) return null;
  return entityToRenderable(preview, ctx);
}

/**
 * S10d — is this entity annotatively hidden at the active CANNOSCALE (so its
 * cache-direct spatial STUB must be dropped from pick / window-select)?
 *
 * TEXT / MTEXT / HATCH carry the typed `AnnotScaleContext[]` directly — hidden
 * when they have NO representation at the active scale and ANNOALLVISIBLE=false.
 *
 * INSERT (Block) is ALSO gated here (round-19 FINDING, typedFirstRenderable:171)
 * with the SAME truth `blockFromEntity` uses when it flattens to `[]`: the
 * per-context resolver PLUS the `annotationScales` allow-list. The render path
 * hides an INSERT by flattening to zero renderables inside `blockFromEntity`,
 * but in cache-direct mode that flatten never runs for the metadata STUB — the
 * stub would stay pickable / window-selectable at the master INSERT point even
 * though nothing is drawn. Gating `Block` here drops that stub, mirroring the
 * TEXT/MTEXT/HATCH stub-drop the pick handlers already do.
 *
 * ATTRIB resolves its visibility in `attdefFromEntity` (returning `[]`),
 * DIMENSION uses the legacy annotative schema, and MULTILEADER picks its own
 * context — all return `false` here so they are never gated by this entry.
 *
 * `resolveTypedAnnotation` reads `ctx.activeAnnotationScale` + `ctx.annoAllVisible`
 * (both `undefined` outside the live lift → resolves to visible, no override),
 * so a default `AdapterContext` never hides anything (golden parity intact).
 *
 * EXPORTED (round-18 FINDING, typedFirstRenderable:127 pick path) so the
 * spatial-pick / window-select handlers can drop the cache-direct metadata
 * STUB of a hidden annotative entity. One truth for "is this typed entity
 * annotatively hidden at the active scale" — the pick path calls the SAME
 * predicate the render gate above uses.
 */
export function typedAnnotativeHidden(
  entity: DrawingEntity,
  ctx: AdapterContext,
): boolean {
  switch (entity.type) {
    case "Text":
    case "MText":
    case "Hatch":
      return !resolveTypedAnnotation(entity.annotativeContexts, ctx).isVisible;
    case "Block": {
      // Round-19/21 FINDING (typedFirstRenderable:171, blockFromEntity:245) —
      // mirror `blockFromEntity`'s flatten-to-`[]` visibility EXACTLY through the
      // SAME one-truth `insertAnnotativeHidden` gate (per-context resolver + the
      // OBJECTSCALE allow-list, with explicit membership as a visible fallback) so
      // a hidden annotative INSERT's cache-direct stub stops being pickable while
      // a member-scale INSERT with a not-yet-seeded context stays pickable.
      const anno = resolveTypedAnnotation(entity.annotativeContexts, ctx);
      return insertAnnotativeHidden(anno, entity.annotationScales, ctx);
    }
    default:
      return false;
  }
}
